<template>
  <TemplateBase>
    <main class="relative z-0 col-span-full row-start-2" :class="{ 'xl:row-start-1': page.image }">
      <!-- Header -->
      <MoleculeHeaderOverview
        class="mb-8 sm:mb-12 md:mb-8"
        :title="page.title"
        :subtitle="page.subtitle"
        :introduction="page.introduction"
        :image="page.image"
        :class="{ 'mt-16 lg:mt-20': !page.image }"
        :short-image="true"
      />

      <OrganismNavigationBreadcrumb
        class="container absolute z-30 top-0 mx-auto p-4 md:relative md:mb-10 md:mt-8 md:px-8 md:py-0"
        :path="breadcrumbs"
        :dark-mode-mobile="!!page.image"
      />

      <section v-if="page.teaserTitle" id="special-page-content" class="relative z-10 mx-auto 2xl:px-56 sm:px-4 md:container lg:px-8">
        <MoleculeMission
          v-if="(page.teaserEmbed && page.teaserEmbed.code) || page.teaserImage"
          :embed="page.teaserEmbed"
          :image="page.teaserImage"
          :caption="page.teaserCaption"
          :title="page.teaserTitle"
          :description="page.teaserDescription"
          :items="[page.teaserFirstReference || null, page.teaserSecondReference || null].filter((reference) => reference)"
          class="teaser-container__molecule mb-16 p-6 bg-white sm:p-8 sm:rounded-xl md:p-12 lg:mb-20 lg:p-16"
        >
          <template #references>
            <div class="flex flex-col items-start pl-5 lg:pl-0">
              <AtomButton
                v-if="page.teaserFirstReference"
                :url="page.teaserFirstReference.url"
                button-type="primary"
                class="flex place-items-center mb-5 px-4 h-12 text-black font-bold leading-none bg-app-lime border-b-2 rounded-xl uppercase"
              >
                {{ page.teaserFirstReference.title }}
                <AtomIconArrowRight class="ml-3 w-3 transform group-hover:translate-x-1 transition duration-200" />
              </AtomButton>

              <AtomLinkWithIcon v-if="page.teaserSecondReference" class="text-black text-lg font-bold" :url="page.teaserSecondReference.url">
                {{ page.teaserSecondReference.title }}
              </AtomLinkWithIcon>
            </div>
          </template>
        </MoleculeMission>
      </section>
      <OrganismMatrix v-if="page.matrix.length" :items="page.matrix" :expiry-date="page.expiryDate || null" class="mb-16" />

      <section v-if="page.donationReference && page.donationReference.url" id="special-page-donation" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <div class="relative">
          <MoleculePicture :image="page.donationReference.image" class="cover-fill hidden w-full h-full lg:block" />
          <div class="bottom-0 right-0 w-full lg:absolute lg:max-w-xl">
            <MoleculeDonation
              :title="page.donationReference.title"
              :subtitle="page.donationReference.subtitle"
              :introduction="page.donationReference.introduction"
              :image="page.donationReference.image"
              :url="page.donationReference.url"
              :price1="page.donationReference.donationPrice1"
              :price2="page.donationReference.donationPrice2"
              :price3="page.donationReference.donationPrice3"
            />
          </div>
        </div>
      </section>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
interface Page {
  // Define specific types according to the page structure
  title: string
  subtitle: string
  introduction: string
  image: string
  teaserTitle?: string
  teaserEmbed?: { code?: string }
  teaserImage?: string
  teaserCaption?: string
  teaserDescription?: string
  teaserFirstReference?: { title: string; url: string }
  teaserSecondReference?: { title: string; url: string }
  matrix: Array<any>
  donationReference?: {
    title: string
    subtitle: string
    introduction: string
    image: string
    url: string
    donationPrice1: number
    donationPrice2: number
    donationPrice3: number
  }
}

interface Props {
  breadcrumbs: Array<any>
  page: Page
}

defineProps<Props>()
</script>
